import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { server } from "../../server";
import Select from "react-select";
import FeeLedger from "./feeLedger";
import { useLocation, useNavigate } from "react-router-dom";
import { getDecodedToken } from "../../auth";
import { paymentModes } from "../../utils";
import QRCode from "qrcode";

function CollectFees() {
  const { state } = useLocation();
  const [admissionNo, setAdmissionNo] = useState(
    () => state?.admissionNo || "",
  );
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [paymentMode, setPaymentMode] = useState("");
  const [paymentDetail, setPaymentDetail] = useState("");
  const [bank, setBank] = useState("");
  const [amount, setAmount] = useState("");
  const navigate = useNavigate();
  const [totalUnpaidAmount, setTotalUnpaidAmount] = useState(0);
  const feeDetails = studentData?.feeDetails;
  const [user, setUser] = useState(null);
  const [feeErrorMessage, setFeeErrorMessage] = useState("");
  const [totalMonthlyFee, setTotalMonthlyFee] = useState(0);
  const [transportFeeTotal, setTransportFeeTotal] = useState(0);
  const [totalAdmissionFee, setTotalAdmissionFee] = useState(0);
  const [totalDevelopmentFee, setTotalDevelopmentFee] = useState(0);
  const [totalLabFee, setTotalLabFee] = useState(0);

  const [qrDataURL, setQrDataURL] = useState("");

  useEffect(() => {
    const generateQR = async () => {
      try {
        const url = await QRCode.toDataURL(admissionNo);
        setQrDataURL(url);
      } catch (err) {
        console.error("QR Code generation failed:", err);
      }
    };

    if (admissionNo) {
      generateQR();
    }
  }, [admissionNo]);

  useEffect(() => {
    const decodedData = getDecodedToken();
    if (decodedData) {
      setUser(decodedData);
    }
  }, []);

  const paymentModeLabels = {
    UPI: "UPI ID",
    NEFT: "UTR No",
    Cheque: "Cheque No",
    "Card Swap": "Card Last 4 Digit No",
    "Net Banking": "Reference ID",
  };

  const bankApplicableModes = [
    "NEFT",
    "Card Swap",
    "Net Banking",
    "Cheque",
    "UPI",
  ];

  const monthOrder = [
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
    "January",
    "February",
    "March",
  ];

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const resetDetails = () => {
    setAdmissionNo("");
    setStudentData(null);
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    if (!admissionNo) {
      toast.error("Enter Admission No", { position: "top-right" });
      setLoading(false);
      return;
    }

    try {
      setError(null);
      setFeeErrorMessage("");

      const res = await axios.post(
        "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/CheckOldSessionPayment",
        {
          admission_no: admissionNo,
        },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = res?.data;

      if (data?.isDue) {
        setFeeErrorMessage(
          "Previous session dues remain unpaid. Please settle them before proceeding.",
        );

        setStudentData(null);
        setTotalUnpaidAmount(0);
        setSelectedMonths([]);
        return;
      }

      const currentRes = await axios.post(
        `${server}/getParticularStudentMongodb`,
        { admission_no: admissionNo },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const currentStudent = currentRes.data?.["body-json"]?.body?.student;

      if (!currentStudent) {
        toast.error("Wrong Admission No", { position: "top-right" });
        return;
      }

      setStudentData(currentStudent);

      const feeDetails = currentStudent?.feeDetails;
      let initialSelectedMonths = [];

      if (feeDetails) {
        const admissionFeeAmt = feeDetails.admissionFee?.paid
          ? 0
          : feeDetails.admissionFee?.amt || 0;

        const developmentFeeAmt = feeDetails.developmentCharges?.paid
          ? 0
          : feeDetails.developmentCharges?.amt || 0;

        setSelectedMonths(initialSelectedMonths);

        calculateTotalUnpaidAmount(
          feeDetails,
          initialSelectedMonths,
          admissionFeeAmt,
          developmentFeeAmt,
        );
      } else {
        setTotalUnpaidAmount(0);
        setSelectedMonths([]);
      }
    } catch (err) {
      setStudentData(null);
      setTotalUnpaidAmount(0);
      setSelectedMonths([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (admissionNo.length === 5) {
      handleSearch();
    }
  }, [admissionNo]);

  const calculateTotalUnpaidAmount = (feeDetails, months) => {
    let unpaidAmount = 0;
    let monthlyFee = 0;
    let transportFee = 0;
    let labFee = 0;

    const admissionFeeApplicable =
      feeDetails.admissionFee?.amt > 0 && !feeDetails.admissionFee?.paid;
    const developmentFeeApplicable =
      feeDetails.developmentCharges?.amt > 0 &&
      !feeDetails.developmentCharges?.paid;

    const admissionAmt = admissionFeeApplicable
      ? toNumber(feeDetails.admissionFee.amt)
      : 0;
    const developmentAmt = developmentFeeApplicable
      ? toNumber(feeDetails.developmentCharges.amt)
      : 0;

    const transportFeeApplicable = feeDetails.transportFee?.amt > 0;
    const labFeeApplicable = feeDetails.labFee?.amt > 0;
    const transportApplicableMonth =
      studentData?.transportFacility?.applicableMonth;
    const applicableIndex = monthOrder.indexOf(transportApplicableMonth);

    months.forEach((month) => {
      const monthDetail = feeDetails.monthPaid.find((m) => m.month === month);

      if (!monthDetail) return;

      // Monthly Fee logic
      if (!monthDetail.monthlyFee?.paid) {
        monthlyFee += toNumber(feeDetails.monthlyFee?.amt);
      }

      // Lab Fee logic (recurring monthly, like Monthly Fee)
      if (labFeeApplicable && !monthDetail.labFee?.paid) {
        labFee += toNumber(feeDetails.labFee?.amt);
      }

      // Transport Fee logic
      const monthIndex = monthOrder.indexOf(month);
      const shouldAddTransport =
        transportFeeApplicable &&
        month !== "June" &&
        applicableIndex !== -1 &&
        monthIndex >= applicableIndex;

      if (shouldAddTransport && !monthDetail.transportFee?.paid) {
        transportFee += toNumber(feeDetails.transportFee?.amt);
      }
    });

    unpaidAmount =
      admissionAmt + developmentAmt + monthlyFee + transportFee + labFee;

    setTotalUnpaidAmount(unpaidAmount);
    setTotalMonthlyFee(monthlyFee);
    setTransportFeeTotal(transportFee);
    setTotalAdmissionFee(admissionAmt);
    setTotalDevelopmentFee(developmentAmt);
    setTotalLabFee(labFee);
  };

  const getNextSelectableMonth = () => {
    const monthPaidList = studentData?.feeDetails?.monthPaid || [];
    const transportFeeApplicable =
      studentData?.feeDetails?.transportFee?.amt > 0;
    const labFeeApplicable = studentData?.feeDetails?.labFee?.amt > 0;
    const transportApplicableMonth =
      studentData?.transportFacility?.applicableMonth;
    const applicableIndex = monthOrder.indexOf(transportApplicableMonth);

    for (let i = 0; i < monthPaidList.length; i++) {
      const m = monthPaidList[i];
      const monthIndex = monthOrder.indexOf(m.month);

      if (selectedMonths.includes(m.month)) continue; // ✅ Skip already selected months

      const isMonthlyUnpaid = !m.monthlyFee?.paid;
      const isTransportUnpaid =
        transportFeeApplicable &&
        m.month !== "June" &&
        applicableIndex !== -1 &&
        monthIndex >= applicableIndex &&
        !m.transportFee?.paid;
      const isLabFeeUnpaid = labFeeApplicable && !m.labFee?.paid;

      if (isMonthlyUnpaid || isTransportUnpaid || isLabFeeUnpaid) {
        return m.month;
      }
    }

    return ""; // All months paid
  };

  const [nextMonth, setNextMonth] = useState(getNextSelectableMonth());

  useEffect(() => {
    setNextMonth(getNextSelectableMonth());
  }, [selectedMonths, studentData]);

  const handleAddMonth = (e) => {
    e.preventDefault();

    if (nextMonth) {
      const newSelectedMonths = [...selectedMonths, nextMonth];
      setSelectedMonths(newSelectedMonths);

      if (studentData?.feeDetails) {
        const admissionFeeAmt = studentData.feeDetails.admissionFee?.paid
          ? 0
          : studentData.feeDetails.admissionFee?.amt || 0;
        const developmentFeeAmt = studentData.feeDetails.developmentCharges
          ?.paid
          ? 0
          : studentData.feeDetails.developmentCharges?.amt || 0;
        calculateTotalUnpaidAmount(
          studentData.feeDetails,
          newSelectedMonths,
          admissionFeeAmt,
          developmentFeeAmt,
        );
      }
    }
  };

  const handleRemoveMonth = () => {
    if (selectedMonths.length > 0) {
      const newSelectedMonths = selectedMonths.slice(0, -1);
      setSelectedMonths(newSelectedMonths);
      console.log("After removing:", newSelectedMonths);

      if (studentData?.feeDetails) {
        const admissionFeeAmt = studentData.feeDetails.admissionFee?.paid
          ? 0
          : studentData.feeDetails.admissionFee?.amt || 0;
        const developmentFeeAmt = studentData.feeDetails.developmentCharges
          ?.paid
          ? 0
          : studentData.feeDetails.developmentCharges?.amt || 0;

        calculateTotalUnpaidAmount(
          studentData.feeDetails,
          newSelectedMonths,
          admissionFeeAmt,
          developmentFeeAmt,
        );
      }
    }
  };

  const amountAdded = selectedPayments.reduce(
    (acc, payment) => acc + payment.amount,
    0,
  );

  const amountLeft = totalUnpaidAmount - amountAdded;

  const handleAddPayment = () => {
    const enteredAmount = parseFloat(amount);

    if (!paymentMode || !amount) {
      toast.error("Select Payment Mode & Enter Amount");
      return;
    }

    if (enteredAmount <= 0) {
      toast.error("Enter Amount Greater Than 0");
      return;
    }

    if (selectedPayments.length >= 1) {
      toast.error("Pay from 1 Payment Mode Only");
      return;
    }

    if (amountAdded + enteredAmount > totalUnpaidAmount) {
      toast.error("Total Amount Cannot Exceed");
      return;
    }

    if (paymentMode !== "Cash" && !paymentDetail) {
      toast.error(`Enter ${paymentModeLabels[paymentMode]}`);
      return;
    }

    if (bankApplicableModes.includes(paymentMode) && !bank) {
      toast.error("Select Bank");
      return;
    }

    setSelectedPayments([
      ...selectedPayments,
      {
        mode: paymentMode,
        amount: enteredAmount,
        detail: paymentMode !== "Cash" ? paymentDetail : null,
        bank: bankApplicableModes.includes(paymentMode) ? bank : null,
      },
    ]);
    setPaymentMode("");
    setAmount("");
    setPaymentDetail("");
    setBank("");
  };

  const handleRemovePayment = (index) => {
    const updatedPayments = selectedPayments.filter((_, i) => i !== index);
    setSelectedPayments(updatedPayments);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!selectedPayments.length) {
      toast.error("Select at least 1 Month");
      setLoading(false);
      return;
    }

    try {
      const isAdmissionPaid = feeDetails.admissionFee?.paid || false;
      const isDevelopmentPaid = feeDetails.developmentCharges?.paid || false;

      const totalAdmissionFee = isAdmissionPaid
        ? 0
        : toNumber(feeDetails.admissionFee?.amt);
      const totalDevelopmentFee = isDevelopmentPaid
        ? 0
        : toNumber(feeDetails.developmentCharges?.amt);

      const totalRequired =
        totalAdmissionFee +
        totalDevelopmentFee +
        totalMonthlyFee +
        transportFeeTotal +
        totalLabFee;

      let paymentModeFormatted = selectedPayments.reduce((acc, payment) => {
        acc[payment.mode] = {
          amount: payment.amount,
          remaining: payment.amount,
          ...(payment.detail ? { detail: payment.detail } : {}),
          ...(payment.bank ? { bank: payment.bank } : {}),
        };
        return acc;
      }, {});

      const totalReceived = Object.values(paymentModeFormatted).reduce(
        (sum, mode) => sum + mode.amount,
        0,
      );

      if (totalReceived !== totalRequired) {
        toast.error(
          `Payment Mismatch! Required: ${totalRequired}, Received: ${totalReceived}`,
        );
        setLoading(false);
        return;
      }

      // Function to Distribute Amount Across Payment Modes
      const distributeAmount = (amountRequired) => {
        let remainingAmount = amountRequired;
        const distributedPayment = {};

        Object.entries(paymentModeFormatted).forEach(([mode, payment]) => {
          if (remainingAmount <= 0) return;

          const allocation = Math.min(payment.remaining, remainingAmount);
          if (allocation > 0) {
            distributedPayment[mode] = {
              amount: allocation,
              ...(payment.detail ? { detail: payment.detail } : {}),
              ...(payment.bank ? { bank: payment.bank } : {}),
            };
            paymentModeFormatted[mode].remaining -= allocation;
            remainingAmount -= allocation;
          }
        });

        return distributedPayment;
      };

      // Prepare Data for Monthly Fees
      const updatedMonthPaid = selectedMonths
        .map((month) => {
          const monthDetail = feeDetails.monthPaid.find(
            (m) => m.month === month,
          );

          let monthlyFeeAmt = 0;
          if (monthDetail && !monthDetail.monthlyFee?.paid) {
            monthlyFeeAmt = toNumber(feeDetails.monthlyFee?.amt);
          }

          let labFeeAmt = 0;
          if (
            toNumber(feeDetails.labFee?.amt) > 0 &&
            monthDetail &&
            !monthDetail.labFee?.paid
          ) {
            labFeeAmt = toNumber(feeDetails.labFee.amt);
          }

          let transportFeeAmt = 0;
          const transportApplicableMonth =
            studentData?.transportFacility?.applicableMonth;
          const monthIndex = monthOrder.indexOf(month);
          const applicableIndex = monthOrder.indexOf(transportApplicableMonth);

          if (
            month !== "June" &&
            monthIndex >= applicableIndex &&
            monthDetail &&
            !monthDetail.transportFee?.paid
          ) {
            transportFeeAmt = toNumber(feeDetails.transportFee?.amt);
          }

          const totalAmount = monthlyFeeAmt + transportFeeAmt + labFeeAmt;
          const monthPaymentMode = distributeAmount(totalAmount);

          const totalDistributed = Object.values(monthPaymentMode).reduce(
            (sum, mode) => sum + mode.amount,
            0,
          );
          if (totalDistributed !== totalAmount) {
            toast.error(
              `Error in payment distribution for ${month}. Expected: ${totalAmount}, Distributed: ${totalDistributed}`,
            );
            setLoading(false);
            return null;
          }

          return {
            month,
            paid: true,
            date: new Date().toISOString().split("T")[0],
            paymentMode: monthPaymentMode,
            TotalAmt: totalAmount,
            monthlyFee: monthlyFeeAmt,
            transportFee: transportFeeAmt,
            labFee: labFeeAmt,
          };
        })
        .filter(Boolean);

      // Final Data Payload
      const finalFormData = {
        admission_no: admissionNo,
        session: studentData?.session,
        collectedBy: user?.userId,
        ...(totalAdmissionFee > 0 &&
          !isAdmissionPaid && {
            admissionFee: {
              paid: true,
              paymentMode: distributeAmount(totalAdmissionFee),
              TotalAmt: totalAdmissionFee,
              date: new Date().toISOString().split("T")[0],
            },
          }),
        ...(totalDevelopmentFee > 0 &&
          !isDevelopmentPaid && {
            developmentCharges: {
              paid: true,
              paymentMode: distributeAmount(totalDevelopmentFee),
              TotalAmt: totalDevelopmentFee,
              date: new Date().toISOString().split("T")[0],
            },
          }),
        monthPaid: updatedMonthPaid,
      };

      // Submit Payment Data
      const response = await axios.post(
        `${server}/feePaymentMongoDb`,
        finalFormData,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const apiStatus = response.data?.["body-json"]?.statusCode;
      let apiMessage = "";
      try {
        apiMessage = JSON.parse(response.data?.["body-json"]?.body)?.message;
      } catch (error) {
        console.error("Error parsing API response body:", error);
      }

      if (
        apiStatus === 200 &&
        apiMessage === "Fee payment recorded successfully."
      ) {
        toast.success("Payment Successful");
        setAdmissionNo("");
        setStudentData(null);
        setSelectedMonths([]);
        setSelectedPayments([]);
        setPaymentMode("");
        setAmount("");
        setTotalUnpaidAmount(0);
        setLoading(false);
        navigate("/monthly-fees");
      } else {
        toast.error("Payment not saved. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      toast.error(
        `Payment Failed: ${error.response?.data?.message || "Unknown Error"}`,
      );
      setLoading(false);
    }
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          {!studentData && (
            <>
              <div className="bg-light">
                <div className="container-fluid">
                  <div className="row g-2">
                    <div className="col-lg-8">
                      <div className="">
                        <div class="m-3 mt-1 pt-1 d-flex justify-content-between">
                          <div class="d-flex align-items-center mt-2">
                            <span
                              onClick={() => window.history.back()}
                              class="back-button me-3 cursor-pointer"
                            >
                              <i className="bi bi-arrow-left fs-3"></i>
                            </span>
                            <nav aria-label="breadcrumb">
                              <h4 className="m-0 lh-sm">Collect Fees</h4>
                              <ol class="breadcrumb pe-3 lh-sm">
                                <li class="breadcrumb-item">
                                  <a href="#">Fees Collection</a>
                                </li>
                                <li class="breadcrumb-item">
                                  <a href="#">Monthly Fees</a>
                                </li>
                                <li
                                  class="breadcrumb-item active"
                                  aria-current="page"
                                >
                                  Collect Fees
                                </li>
                              </ol>
                            </nav>
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-8 col-12 align-items-center justify-content-center bg-white p-4 rounded-4">
                        <div className="d-flex flex-column">
                          <div className="d-flex flex-column gap-1">
                            <h1 className="fw-medium mb-0 fs-4 lh-1">
                              Enter Admission No.
                              <br />
                              <span className="mb-0 text-secondary fw-normal fs-5">
                                Admission No. to retrieve the student's academic
                                & fee details.
                              </span>
                            </h1>
                          </div>
                          <div className="col-7 mt-3">
                            <form
                              className="d-flex align-items-center"
                              onSubmit={handleSearch}
                            >
                              <span className="position-absolute ps-4">
                                <i className="bi bi-person fs-4" />
                              </span>
                              <input
                                type="search"
                                value={admissionNo}
                                autoFocus
                                onChange={(e) => setAdmissionNo(e.target.value)}
                                className="form-control ps-7 fs-5 lg-search-box"
                                maxLength={5}
                                placeholder="Enter Admission No"
                              />
                            </form>
                          </div>
                        </div>

                        {feeErrorMessage && (
                          <div
                            className="alert alert-danger lh-1 fs-5 rounded-3 mb-0 text-capitalize mt-2"
                            role="alert"
                          >
                            {feeErrorMessage}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {studentData && (
            <div className="mt-1">
              <div class="container-fluid">
                <div className="row g-2">
                  <div className="col-lg-8">
                    <div className="">
                      <div class="m-3 mt-1 d-flex justify-content-between">
                        <div class="d-flex align-items-center mt-2">
                          <span
                            onClick={() => window.history.back()}
                            class="back-button me-3 cursor-pointer"
                          >
                            <i className="bi bi-arrow-left fs-3"></i>
                          </span>
                          <nav aria-label="breadcrumb">
                            <h4 className="m-0 lh-sm">Collect Fees</h4>
                            <ol class="breadcrumb pe-3 lh-sm">
                              <li class="breadcrumb-item">
                                <a href="#">Fees Collection</a>
                              </li>
                              <li class="breadcrumb-item">
                                <a href="#">Monthly Fees</a>
                              </li>
                              <li
                                class="breadcrumb-item active"
                                aria-current="page"
                              >
                                Collect Fees
                              </li>
                            </ol>
                          </nav>
                        </div>
                      </div>
                    </div>

                    <form
                      onSubmit={handleSubmit}
                      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    >
                      <div class="px-5 py-3 bg-white rounded-4">
                        <div
                          class="accordion accordion-flush"
                          id="accordionExample"
                        >
                          <div id="headingTwo">
                            <h3 class="m-0 fs-list">
                              <a
                                href="#"
                                class="d-flex align-items-center text-inherit"
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseThree"
                                aria-controls="collapseThree"
                              >
                                <span class="me-auto fs-5">
                                  1. Fee Structure{" "}
                                  <span className="ms-3 text-secondary">
                                    (04/04)
                                  </span>
                                </span>
                                <div className="d-flex align-items-center">
                                  <span class="badge bg-danger-soft me-5 px-3">
                                    Locked
                                  </span>
                                  <span className="collapse-toggle">
                                    <i class="bi bi-chevron-down"></i>
                                  </span>
                                </div>
                              </a>
                            </h3>

                            <div
                              id="collapseThree"
                              class="collapse"
                              aria-labelledby="headingThree"
                              data-bs-parent="#accordionExample"
                            >
                              <div className="row g-2 mt-2 mb-3">
                                <div className="col-lg-6 col-12">
                                  <label className="form-label mb-1 ms-3">
                                    Admission Fee{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control text-dark"
                                    placeholder="..."
                                    name="admissionFee"
                                    value={
                                      studentData?.feeDetails?.admissionFee
                                        ?.paid
                                        ? 0
                                        : studentData?.feeDetails?.admissionFee
                                            ?.amt
                                    }
                                    readOnly
                                  />
                                </div>
                                <div className="col-lg-6 col-12">
                                  <label className="form-label mb-1 ms-3">
                                    Development Charges{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control text-dark"
                                    placeholder="..."
                                    name="developmentCharges"
                                    value={
                                      studentData?.feeDetails
                                        ?.developmentCharges?.paid
                                        ? 0
                                        : studentData?.feeDetails
                                            ?.developmentCharges?.amt
                                    }
                                    readOnly
                                  />
                                </div>
                                <div className="col-lg-6 col-12">
                                  <label className="form-label mb-1 ms-3">
                                    Monthly Fee{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control text-dark"
                                    placeholder="..."
                                    name="monthlyFee"
                                    value={
                                      studentData?.feeDetails?.monthlyFee?.amt
                                    }
                                    readOnly
                                  />
                                </div>
                                {studentData?.feeDetails?.labFee?.amt > 0 && (
                                  <div className="col-lg-6 col-12">
                                    <label className="form-label mb-1 ms-3">
                                      Lab Fee{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control text-dark"
                                      placeholder="..."
                                      name="labFee"
                                      value={
                                        studentData?.feeDetails?.labFee?.amt
                                      }
                                      readOnly
                                    />
                                  </div>
                                )}
                                <div className="col-lg-6 col-12">
                                  <label class="form-label mb-1 ms-3">
                                    Transport Fee
                                    <span class="badge bg-dark-soft px-3 ms-3">
                                      {
                                        studentData?.transportFacility
                                          ?.applicableMonth
                                      }
                                    </span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control text-dark"
                                    name="transportFee"
                                    value={
                                      studentData?.feeDetails?.transportFee?.amt
                                    }
                                    placeholder="..."
                                    readOnly
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="px-5 py-3 mt-1 bg-white rounded-4">
                        <div
                          class="accordion accordion-flush"
                          id="accordionExample"
                        >
                          <div id="headingFour">
                            <h3 class="m-0 fs-list">
                              <a
                                href="#"
                                class="d-flex align-items-center text-inherit"
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseFour"
                                aria-controls="collapseFour"
                              >
                                <span class="me-auto fs-5">
                                  2. Unpaid Months
                                </span>
                                <div className="d-flex align-items-center">
                                  <span class="badge bg-success-soft me-5 px-3">
                                    Editable
                                  </span>
                                  <span className="collapse-toggle">
                                    <i class="bi bi-chevron-down"></i>
                                  </span>
                                </div>
                              </a>
                            </h3>

                            <div
                              id="collapseFour"
                              class="collapse"
                              aria-labelledby="headingFour"
                              data-bs-parent="#accordionExample"
                            >
                              <div className="row g-2 mt-2 mb-3">
                                <div className="col-lg-6 col-12">
                                  <label className="form-label mb-1 ms-3">
                                    Month <span className="text-danger">*</span>
                                  </label>
                                  <input
                                    className="form-control text-dark"
                                    value={nextMonth || "No More Months"}
                                    readOnly
                                  />
                                  <button
                                    className="btn btn-danger px-5 mt-3 mb-2 fs-6 fw-semibold rounded-pill"
                                    onClick={handleAddMonth}
                                  >
                                    Add Month{" "}
                                    <i className="bi bi-plus-circle-fill ms-2"></i>
                                  </button>
                                </div>

                                <div className="col-lg-6 col-12 d-flex justify-content-end align-items-center">
                                  <div className="d-flex flex-column ms-3">
                                    {selectedMonths.map((month, index) => (
                                      <div className="p-2 d-flex align-items-center rounded-5 bg-light mb-1 unpaid-months">
                                        <div className="ms-n3 me-2">
                                          {index ===
                                            selectedMonths.length - 1 &&
                                            selectedMonths.length > 0 && (
                                              <i
                                                className="bi bi-x-circle-fill fs-3 text-dark cursor-pointer"
                                                onClick={handleRemoveMonth}
                                              ></i>
                                            )}
                                        </div>
                                        <div className="me-2">
                                          <h5
                                            key={index}
                                            className="px-3 lh-sm m-0 text-secondary fw-medium"
                                          >
                                            {month}
                                          </h5>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="px-5 py-3 mt-1 bg-white rounded-4">
                        <div
                          class="accordion accordion-flush"
                          id="accordionExample"
                        >
                          <div id="headingFive">
                            <h3 class="m-0 fs-list">
                              <a
                                href="#"
                                class="d-flex align-items-center text-inherit"
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseFive"
                                aria-controls="collapseFive"
                              >
                                <span class="me-auto fs-5">
                                  3. Payment Mode
                                </span>
                                <div className="d-flex align-items-center">
                                  <span class="badge bg-success-soft me-5 px-3">
                                    Editable
                                  </span>
                                  <span className="collapse-toggle">
                                    <i class="bi bi-chevron-down"></i>
                                  </span>
                                </div>
                              </a>
                            </h3>

                            <div
                              id="collapseFive"
                              class="collapse"
                              aria-labelledby="headingFive"
                              data-bs-parent="#accordionExample"
                            >
                              <div className="row g-2 mt-2 mb-3">
                                <div className="col-12">
                                  <div className="row g-2">
                                    <div className="col-6">
                                      <label className="form-label mb-1 ms-3">
                                        Payment Mode{" "}
                                        <span className="text-danger">*</span>
                                      </label>
                                      <Select
                                        className="text-dark"
                                        name="paymentMode"
                                        value={{
                                          value: paymentMode,
                                          label: paymentMode,
                                        }}
                                        onChange={(selectedOption) =>
                                          setPaymentMode(selectedOption.value)
                                        }
                                        options={paymentModes.map((mode) => ({
                                          value: mode,
                                          label: mode,
                                        }))}
                                        placeholder="Select Mode"
                                      />
                                    </div>

                                    {paymentMode && paymentMode !== "Cash" && (
                                      <div className="col-6">
                                        <label className="form-label mb-1 ms-3">
                                          {paymentModeLabels[paymentMode]}{" "}
                                          <span className="text-danger">*</span>
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control text-dark"
                                          placeholder={`Enter ${paymentModeLabels[paymentMode]}`}
                                          value={paymentDetail}
                                          onChange={(e) =>
                                            setPaymentDetail(e.target.value)
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {bankApplicableModes.includes(paymentMode) && (
                                  <>
                                    <div className="col-6 offset-right-6">
                                      <label className="form-label mb-1 ms-3">
                                        Bank{" "}
                                        <span className="text-danger">*</span>
                                      </label>
                                      <Select
                                        className="text-dark"
                                        name="bank"
                                        value={{
                                          value: bank,
                                          label:
                                            bank === "HDFC"
                                              ? "HDFC Bank"
                                              : "Punjab National Bank (PNB)",
                                        }}
                                        onChange={(selectedOption) =>
                                          setBank(selectedOption.value)
                                        }
                                        options={[
                                          { value: "HDFC", label: "HDFC Bank" },
                                          {
                                            value: "PNB",
                                            label: "Punjab National Bank (PNB)",
                                          },
                                        ]}
                                        placeholder="Select Bank"
                                      />
                                    </div>
                                  </>
                                )}
                                <div className="col-lg-6 col-12">
                                  <div className="col-12">
                                    <label class="form-label mb-1 ms-3">
                                      Amount
                                      <span class="badge bg-danger-soft px-3 ms-3">
                                        Remaining &mdash;{" "}
                                        {amountLeft > 0 ? amountLeft : 0}
                                      </span>
                                    </label>
                                    <input
                                      type="number"
                                      className="form-control text-dark"
                                      name="amount"
                                      placeholder="Enter Amount"
                                      value={amount}
                                      onChange={(e) =>
                                        setAmount(e.target.value)
                                      }
                                    />
                                  </div>
                                  <span
                                    class="btn btn-danger px-5 mt-3 mb-2 fs-6 fw-semibold rounded-pill cursor-pointer"
                                    onClick={handleAddPayment}
                                  >
                                    Add Payment
                                    <i className="bi bi-plus-circle-fill ms-2"></i>
                                  </span>
                                </div>
                                <div className="col-lg-6 col-12 d-flex justify-content-end align-items-center">
                                  <div className="d-flex flex-column ms-3">
                                    {selectedPayments.map((payment, index) => (
                                      <div className="p-2 d-flex align-items-center rounded-4 bg-light mb-1">
                                        <div className="ms-n3 me-2">
                                          <i
                                            className="bi bi-x-circle-fill fs-3 text-dark cursor-pointer"
                                            onClick={() =>
                                              handleRemovePayment(index)
                                            }
                                          ></i>
                                        </div>
                                        <div className="me-2">
                                          <h5
                                            className="px-3 py-2 lh-sm m-0 text-secondary"
                                            key={index}
                                          >
                                            Amount &mdash; ₹{payment.amount}
                                            <br />
                                            Mode &mdash; {payment.mode}
                                            <br />
                                            {payment.detail && (
                                              <>
                                                {
                                                  paymentModeLabels[
                                                    payment.mode
                                                  ]
                                                }{" "}
                                                &mdash; {payment.detail}
                                                <br />
                                              </>
                                            )}
                                            {payment.bank && (
                                              <>Bank &mdash; {payment.bank}</>
                                            )}
                                          </h5>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="px-5 py-3 mt-1 bg-white rounded-4">
                        <div
                          class="accordion accordion-flush"
                          id="accordionExample"
                        >
                          <div id="headingTwo">
                            <h3 class="m-0 fs-list">
                              <a
                                href="#"
                                class="d-flex align-items-center text-inherit"
                                data-bs-toggle="collapse"
                                data-bs-target="#collapseTwo"
                                aria-controls="collapseTwo"
                              >
                                <span class="me-auto fs-5">
                                  4. Fees Payment Ledger
                                </span>
                                <div className="d-flex align-items-center">
                                  <span className="badge me-5 px-3 bg-info-soft">
                                    As per Fee Payment
                                  </span>
                                  <span className="collapse-toggle">
                                    <i class="bi bi-chevron-down"></i>
                                  </span>
                                </div>
                              </a>
                            </h3>

                            <div
                              id="collapseTwo"
                              class="collapse"
                              aria-labelledby="headingTwo"
                              data-bs-parent="#accordionExample"
                            >
                              <FeeLedger feeDetails={studentData.feeDetails} />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="my-3 d-flex float-end me-5">
                        <button
                          type="submit"
                          class="btn btn-dark mt-2 px-3 me-1"
                          onClick={resetDetails}
                        >
                          <i className="bi bi-person"></i>
                        </button>
                        <button
                          type="submit"
                          class="btn btn-success mt-2 px-6"
                          disabled={
                            loading ||
                            selectedPayments.length === 0 ||
                            amountLeft !== 0
                          }
                        >
                          {loading ? (
                            <div
                              class="spinner-border spinner-border-sm"
                              role="status"
                            ></div>
                          ) : (
                            "Submit"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  <div className="col-lg-4">
                    <div class="p-5 pt-4 bg-white rounded-4 mt-2">
                      <div className="">
                        <div className="d-flex">
                          <div>
                            <img
                              src={
                                studentData?.studentDetails?.studentImage ||
                                `/assets/images/profile.png`
                              }
                              alt="Image"
                              width="90"
                              height="90"
                              className="object-fit-cover rounded-5 mt-3 border border-3 border-white"
                            />
                            <div class="icon-shape icon-xl bg-white border rounded-circle position-absolute ms-n4 mt-n1">
                              <img
                                src={qrDataURL}
                                className="rounded-circle"
                                alt="QR"
                                width="40"
                              />
                            </div>
                          </div>

                          <div className="ps-3 rounded-bottom-5 bg-white pt-8">
                            <div>
                              <h3 class="m-0 fs-4 lh-1">
                                {studentData?.studentDetails?.name
                                  .toLowerCase()
                                  .replace(/\b\w/g, (char) =>
                                    char.toUpperCase(),
                                  )}
                                <i class="bi bi-check-circle fs-5 text-primary ms-2"></i>
                              </h3>
                              <p className="m-0 fs-5 lh-1">
                                {studentData?.studentDetails?.admission_no}{" "}
                                &mdash; {studentData?.studentDetails?.studentId}
                                <span className="badge bg-success-soft ms-3">
                                  {studentData?.currentClass},{" "}
                                  {studentData?.studentDetails?.currentSection}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="text-center">
                          {studentData?.transportFacility?.busStop && (
                            <p className="m-0 lh-sm mt-2 text-capitalize bg-light py-2 rounded-4 text-dark px-5">
                              Stoppage &mdash;{" "}
                              {studentData?.transportFacility?.busStop}
                              <br />
                              Bus No &mdash;{" "}
                              {studentData?.transportFacility?.busNumber ||
                                "NA"}
                              <br />
                              Applicable Month &mdash;{" "}
                              {studentData?.transportFacility
                                ?.applicableMonth || "NA"}
                            </p>
                          )}

                          <div className="d-flex flex-wrap justify-content-center align-items-center rounded-lg text-sm gap-1 py-2 mt-2">
                            <div>
                              <img
                                src="/assets/images/fee.png"
                                width={totalUnpaidAmount === 0 ? 60 : 50}
                              />
                            </div>

                            {totalMonthlyFee > 0 && (
                              <span className="btn btn-tag btn-sm bg-success-subtle border border-success rounded-4 py-2 px-4 lh-sm text-success-emphasis">
                                Monthly Fee
                                <br />₹{totalMonthlyFee.toLocaleString()}
                              </span>
                            )}
                            {transportFeeTotal > 0 && (
                              <span className="btn btn-tag btn-sm bg-info-subtle border border-info rounded-4 py-2 px-4 lh-sm text-info-emphasis">
                                Transport Fee
                                <br />₹{transportFeeTotal.toLocaleString()}
                              </span>
                            )}
                            {totalAdmissionFee > 0 && (
                              <span className="btn btn-tag btn-sm bg-secondary-subtle border border-secondary rounded-4 py-2 px-4 lh-sm text-secondary-emphasis">
                                Admission Fee
                                <br />₹{totalAdmissionFee.toLocaleString()}
                              </span>
                            )}
                            {totalDevelopmentFee > 0 && (
                              <span className="btn btn-tag btn-sm bg-danger-subtle border border-danger rounded-4 py-2 px-4 lh-sm text-danger-emphasis">
                                Development Charges
                                <br />₹{totalDevelopmentFee.toLocaleString()}
                              </span>
                            )}
                            {totalLabFee > 0 && (
                              <span className="btn btn-tag btn-sm bg-warning-subtle border border-warning rounded-4 py-2 px-4 lh-sm text-warning-emphasis">
                                Lab Fee
                                <br />₹{totalLabFee.toLocaleString()}
                              </span>
                            )}
                          </div>

                          <p className="mt-1 mb-0">Total Payable</p>
                          <h3 class="m-0 lh-sm h2">
                            <i className="bi bi-currency-rupee"></i>
                            {totalUnpaidAmount.toLocaleString()}
                          </h3>
                          <p className="mt-3 mb-0 text-danger lh-sm">
                            (+91) {studentData?.studentDetails?.phoneNo}
                            <br />
                            <span className="text-lowercase">
                              {studentData?.parentDetails[0]?.email}
                            </span>
                          </p>
                          <p className="m-0 lh-sm mt-2 text-capitalize">
                            {studentData?.presentAddress?.address
                              .toLowerCase()
                              .replace(/\b\w/g, (char) => char.toUpperCase())}
                            <br />
                            {studentData?.presentAddress?.city} -{" "}
                            {studentData?.presentAddress?.state}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}

export default CollectFees;
